import React, { useState, useEffect } from 'react';
import { apiService } from './services/api';
import './style/PageStyle/ActivityLog.css'; 

const ITEMS_PER_PAGE = 15;

const ActivityLog = () => {
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // --- FILTERS STATE ---
  const [filterType, setFilterType] = useState('ALL');
  const [selectedUser, setSelectedUser] = useState('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Derived state for dropdowns
  const [uniqueUsers, setUniqueUsers] = useState([]);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadActivities();
  }, []);

  // Reset pagination when any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, selectedUser, startDate, endDate]);

  const loadActivities = async () => {
    try {
      const data = await apiService.getActivityLogs();
      setActivities(data);

      // Extract unique users for the dropdown
      const users = [...new Set(data.map(item => item.username || item.user))].filter(Boolean).sort();
      setUniqueUsers(users);

    } catch (error) {
      console.error("Failed to load logs", error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- 1. MASTER FILTER LOGIC ---
  const filteredActivities = activities.filter(act => {
    const actionType = act.action_type || act.type;
    const username = act.username || act.user;
    const timestamp = new Date(act.created_at || act.timestamp);

    // A. Filter by Type
    if (filterType !== 'ALL' && actionType !== filterType) return false;

    // B. Filter by User
    if (selectedUser !== 'ALL' && username !== selectedUser) return false;

    // C. Filter by Date Range
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0); // Start of selected day
      if (timestamp < start) return false;
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // End of selected day
      if (timestamp > end) return false;
    }

    return true;
  });

  // --- 2. PAGINATION LOGIC ---
  const totalPages = Math.ceil(filteredActivities.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentActivities = filteredActivities.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // --- 3. EXPORT TO CSV ---
  const handleExport = () => {
    if (filteredActivities.length === 0) {
      alert("No data to export based on current filters.");
      return;
    }

    // Define CSV Headers
    const headers = ["Time", "User", "IP Address", "Action Type", "Description"];
    
    // Map data to CSV rows
    const rows = filteredActivities.map(log => {
      const time = new Date(log.created_at || log.timestamp).toLocaleString().replace(/,/g, ''); // Remove commas to prevent CSV break
      const user = log.username || log.user || 'N/A';
      const ip = log.ip_address || log.ipAddress || 'N/A';
      const type = log.action_type || log.type || 'N/A';
      const desc = (log.description || '').replace(/,/g, ';'); // Replace commas in description

      return [time, user, ip, type, desc].join(",");
    });

    // Combine headers and rows
    const csvContent = "data:text/csv;charset=utf-8," 
      + headers.join(",") + "\n" 
      + rows.join("\n");

    // Create download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `activity_log_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper for badge colors
  const getTypeBadgeClass = (type) => {
    if (!type) return 'badge-default';
    switch (type.toUpperCase()) {
      case 'LOGIN': return 'badge-login';
      case 'DELETE': return 'badge-danger';
      case 'UPLOAD': return 'badge-success';
      case 'DOWNLOAD': return 'badge-info';
      case 'UPDATE': return 'badge-warning';
      case 'USER_EDIT': return 'badge-warning';
      case 'ORG_UPDATE': return 'badge-warning';
      default: return 'badge-default';
    }
  };

  // Handlers for page turning
  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(prev => prev - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
  };

  return (
    <div className="activity-page">
      <div className="activity-header-row">
        <h1>System Activity Log</h1>
        
        {/* EXPORT BUTTON */}
        <button className="btn-export" onClick={handleExport} style={{ marginLeft: 'auto', padding: '8px 16px', cursor: 'pointer', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px' }}>
          📥 Export CSV
        </button>
      </div>

      {/* FILTERS BAR */}
      <div className="activity-filters" style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'center' }}>
        
        {/* Action Type Filter */}
        <div className="filter-group">
          <label style={{ marginRight: '5px', fontWeight: 'bold' }}>Action:</label>
          <select onChange={(e) => setFilterType(e.target.value)} value={filterType}>
            <option value="ALL">All Activities</option>
            <option value="UPLOAD">Uploads</option>
            <option value="DOWNLOAD">Downloads</option>
            <option value="UPDATE">Document Updates</option>
            <option value="DELETE">Deletions</option>
            <option value="LOGIN">Logins</option>
            <option value="USER_EDIT">User Management</option>
            <option value="ORG_UPDATE">Organisation Management</option>
          </select>
        </div>

        {/* User Filter */}
        <div className="filter-group">
          <label style={{ marginRight: '5px', fontWeight: 'bold' }}>User:</label>
          <select onChange={(e) => setSelectedUser(e.target.value)} value={selectedUser}>
            <option value="ALL">All Users</option>
            {uniqueUsers.map(user => (
              <option key={user} value={user}>{user}</option>
            ))}
          </select>
        </div>

        {/* Date Range Filter */}
        <div className="filter-group">
          <label style={{ marginRight: '5px', fontWeight: 'bold' }}>From:</label>
          <input 
            type="date" 
            value={startDate} 
            onChange={(e) => setStartDate(e.target.value)} 
            style={{ padding: '5px' }}
          />
        </div>

        <div className="filter-group">
          <label style={{ marginRight: '5px', fontWeight: 'bold' }}>To:</label>
          <input 
            type="date" 
            value={endDate} 
            onChange={(e) => setEndDate(e.target.value)} 
            style={{ padding: '5px' }}
          />
        </div>

        {/* Reset Filters Button */}
        {(filterType !== 'ALL' || selectedUser !== 'ALL' || startDate || endDate) && (
          <button 
            onClick={() => {
              setFilterType('ALL');
              setSelectedUser('ALL');
              setStartDate('');
              setEndDate('');
            }}
            style={{ padding: '5px 10px', cursor: 'pointer', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px' }}
          >
            Reset
          </button>
        )}
      </div>

      <div className="activity-table-card">
        <table className="activity-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>User</th>
              <th>IP Address</th>
              <th>Action Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan="5" className="text-center">Loading...</td></tr>
            ) : currentActivities.length > 0 ? (
              currentActivities.map(log => {
                const actionType = log.action_type || log.type;
                const ip = log.ip_address || log.ipAddress || 'N/A';
                const timestamp = log.created_at || log.timestamp;
                const username = log.username || log.user;

                return (
                  <tr key={log.id}>
                    <td className="col-time">
                      {new Date(timestamp).toLocaleString()}
                    </td>
                    <td className="col-user">
                      <span className="user-avatar-small">👤</span> {username}
                    </td>
                    <td className="col-ip">
                      <span className="ip-address">{ip}</span>
                    </td>
                    <td>
                      <span className={`status-badge ${getTypeBadgeClass(actionType)}`}>
                        {actionType}
                      </span>
                    </td>
                    <td>{log.description}</td>
                  </tr>
                );
              })
            ) : (
              <tr><td colSpan="5" className="text-center">No records found matching filters.</td></tr>
            )}
          </tbody>
        </table>

        {/* Pagination Controls */}
        {!isLoading && filteredActivities.length > 0 && (
          <div className="pagination-controls">
            <button 
              className="page-btn" 
              onClick={handlePrev} 
              disabled={currentPage === 1}
            >
              &laquo; Previous
            </button>
            
            <span className="page-info">
              Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
            </span>
            
            <button 
              className="page-btn" 
              onClick={handleNext} 
              disabled={currentPage === totalPages}
            >
              Next &raquo;
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLog;
